<template>
  <!-- Справочник определяет документы, принимаемые в рамках текущей кампании. -->
  <v-card class="admission-documents" elevation="0">
    <v-card-title class="admission-documents__header">
      <div>
        <h2 class="admission-documents__title">Документы</h2>
        <p class="admission-documents__description">
          Настройте перечень документов, принимаемых от абитуриентов.
        </p>
      </div>

      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-plus"
        :disabled="loading"
        @click="openEditor()"
      >
        Добавить документ
      </v-btn>
    </v-card-title>

    <v-card-text class="admission-documents__content">
      <v-alert
        v-if="error"
        class="admission-documents__alert"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <div v-if="loading">
        <v-skeleton-loader
          v-for="index in 4"
          :key="index"
          type="list-item-two-line"
        />
      </div>

      <div v-else-if="!items.length" class="admission-documents__empty">
        <v-icon icon="mdi-file-document-plus-outline" size="44" />
        <div>
          <strong>Документы пока не добавлены</strong>
          <span>Добавьте первый тип документа для приемной комиссии.</span>
        </div>
      </div>

      <div v-else class="admission-documents__list">
        <article
          v-for="item in items"
          :key="item.id"
          class="admission-documents__item"
        >
          <div class="admission-documents__item-icon">
            <v-icon icon="mdi-file-document-outline" size="23" />
          </div>

          <div class="admission-documents__item-content">
            <div class="admission-documents__item-name">
              {{ item.name }}
            </div>
            <div v-if="item.comment" class="admission-documents__item-comment">
              {{ item.comment }}
            </div>
            <div class="admission-documents__item-meta">
              <v-chip
                v-if="item.allowScanUpload"
                color="success"
                variant="tonal"
                size="x-small"
                prepend-icon="mdi-upload-outline"
              >
                Загрузка сканов разрешена
              </v-chip>
              <v-chip
                v-if="item.usageCount"
                color="primary"
                variant="tonal"
                size="x-small"
              >
                Используется: {{ item.usageCount }}
              </v-chip>
            </div>
          </div>

          <div class="admission-documents__item-actions">
            <v-btn
              icon="mdi-pencil-outline"
              variant="text"
              size="small"
              aria-label="Редактировать документ"
              @click="openEditor(item)"
            />

            <v-tooltip :text="deleteTooltip(item)" location="top">
              <template #activator="{ props }">
                <span v-bind="props">
                  <v-btn
                    icon="mdi-delete-outline"
                    variant="text"
                    color="error"
                    size="small"
                    aria-label="Удалить документ"
                    :disabled="!item.canDelete"
                    @click="openDeleteDialog(item)"
                  />
                </span>
              </template>
            </v-tooltip>
          </div>
        </article>
      </div>
    </v-card-text>

    <v-dialog v-model="editorDialog" max-width="680" persistent>
      <v-card>
        <v-card-title>
          {{ editedItem.id ? 'Редактирование документа' : 'Новый документ' }}
        </v-card-title>

        <v-card-text>
          <v-alert
            v-if="dialogError"
            class="mb-5"
            type="error"
            variant="tonal"
          >
            {{ dialogError }}
          </v-alert>

          <div class="admission-documents__form">
            <v-text-field
              v-model.trim="editedItem.name"
              label="Название документа"
              variant="outlined"
              autofocus
              required
              hide-details="auto"
              @keydown.ctrl.enter="saveItem"
            />

            <v-textarea
              v-model="editedItem.comment"
              label="Комментарий для оператора"
              variant="outlined"
              rows="3"
              auto-grow
              hide-details="auto"
              @keydown.ctrl.enter="saveItem"
            />

            <v-switch
              v-model="editedItem.allowScanUpload"
              color="success"
              label="Разрешить загрузку сканов"
              inset
              hide-details
            />
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="saving" @click="closeEditor">
            Отмена
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="saving"
            @click="saveItem"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500" persistent>
      <v-card>
        <v-card-title>Удалить тип документа?</v-card-title>
        <v-card-text>
          <v-alert
            v-if="deleteError"
            class="mb-5"
            type="error"
            variant="tonal"
          >
            {{ deleteError }}
          </v-alert>
          <p class="mb-2">
            Документ будет удален из перечня приемной комиссии.
          </p>
          <strong>{{ selectedItem ? selectedItem.name : '' }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="deleting" @click="closeDeleteDialog">
            Отмена
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="deleting"
            @click="deleteItem"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'DocumentsSection',
  data() {
    return {
      items: [],
      loading: true,
      saving: false,
      deleting: false,
      error: '',
      dialogError: '',
      deleteError: '',
      editorDialog: false,
      deleteDialog: false,
      selectedItem: null,
      editedItem: {
        id: 0,
        name: '',
        comment: '',
        allowScanUpload: false
      }
    };
  },
  created() {
    this.loadItems();
  },
  methods: {
    loadItems() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getAdmissionDocuments'
        )
        .then((response) => {
          this.items = response.data.items || [];
        })
        .catch((error) => {
          this.error = this.getErrorMessage(
            error,
            'Не удалось загрузить перечень документов.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openEditor(item = null) {
      this.dialogError = '';
      this.editedItem = item
        ? {
          id: Number(item.id),
          name: item.name || '',
          comment: item.comment || '',
          allowScanUpload: Boolean(item.allowScanUpload)
        }
        : {
          id: 0,
          name: '',
          comment: '',
          allowScanUpload: false
        };
      this.editorDialog = true;
    },
    closeEditor() {
      this.editorDialog = false;
      this.dialogError = '';
    },
    saveItem() {
      const name = (this.editedItem.name || '').trim();
      const comment = (this.editedItem.comment || '').trim();

      if (!name) {
        this.dialogError = 'Введите название документа.';
        return;
      }

      this.saving = true;
      this.dialogError = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/saveAdmissionDocument',
          new URLSearchParams({
            id: String(this.editedItem.id || 0),
            name,
            comment,
            allowScanUpload: this.editedItem.allowScanUpload ? '1' : '0'
          })
        )
        .then((response) => {
          const savedItem = response.data.item;
          const index = this.items.findIndex(
            (item) => Number(item.id) === Number(savedItem.id)
          );

          if (index === -1) {
            this.items.push(savedItem);
          } else {
            this.items.splice(index, 1, savedItem);
          }

          this.sortItems();
          this.closeEditor();
        })
        .catch((error) => {
          this.dialogError = this.getErrorMessage(
            error,
            'Не удалось сохранить документ.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    deleteTooltip(item) {
      if (item.allowScanUpload) {
        return 'Перед удалением отключите загрузку сканов';
      }
      if (item.usageCount) {
        return 'Документ используется в данных абитуриентов';
      }
      return 'Удалить документ';
    },
    openDeleteDialog(item) {
      // Удаление доступно только для неиспользуемого документа с отключённой
      // загрузкой сканов; сервер повторно проверяет оба ограничения.
      if (!item.canDelete) {
        return;
      }

      this.selectedItem = item;
      this.deleteError = '';
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteError = '';
      this.selectedItem = null;
    },
    deleteItem() {
      if (!this.selectedItem) {
        return;
      }

      this.deleting = true;
      this.deleteError = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/deleteAdmissionDocument',
          new URLSearchParams({ id: String(this.selectedItem.id) })
        )
        .then(() => {
          this.items = this.items.filter(
            (item) => Number(item.id) !== Number(this.selectedItem.id)
          );
          this.closeDeleteDialog();
        })
        .catch((error) => {
          this.deleteError = this.getErrorMessage(
            error,
            'Не удалось удалить документ.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    sortItems() {
      // Документы, для которых абитуриент может загрузить скан, показываются
      // выше остальных как наиболее значимые для публичной анкеты.
      this.items.sort((first, second) => {
        const scanDifference =
          Number(Boolean(second.allowScanUpload))
          - Number(Boolean(first.allowScanUpload));

        return scanDifference
          || first.name.localeCompare(second.name, 'ru');
      });
    },
    getErrorMessage(error, fallback) {
      const responseData = error && error.response
        ? error.response.data
        : null;

      if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
      }
      if (responseData && typeof responseData.message === 'string') {
        return responseData.message;
      }

      return fallback;
    }
  }
};
</script>

<style scoped>
.admission-documents {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}

.admission-documents__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 28px 18px;
  border-bottom: 1px solid #edf0f4;
  white-space: normal;
}

.admission-documents__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
}

.admission-documents__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.admission-documents__content {
  padding: 24px 28px 28px;
}

.admission-documents__alert {
  margin-bottom: 20px;
}

.admission-documents__list {
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
}

.admission-documents__item {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  min-height: 74px;
  padding: 12px 14px;
  border-bottom: 1px solid #edf0f4;
}

.admission-documents__item:last-child {
  border-bottom: 0;
}

.admission-documents__item-icon {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 10px;
  background: #eaf3fc;
  color: #1565c0;
}

.admission-documents__item-content {
  min-width: 0;
}

.admission-documents__item-name {
  color: #344054;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.admission-documents__item-comment {
  margin-top: 3px;
  color: #667085;
  font-size: 13px;
  line-height: 1.4;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.admission-documents__item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
}

.admission-documents__item-meta:empty {
  display: none;
}

.admission-documents__item-actions {
  display: flex;
  align-items: center;
}

.admission-documents__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 190px;
  color: #667085;
}

.admission-documents__empty strong,
.admission-documents__empty span {
  display: block;
}

.admission-documents__empty strong {
  margin-bottom: 4px;
  color: #344054;
}

.admission-documents__form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

@media (max-width: 600px) {
  .admission-documents__header {
    align-items: stretch;
    flex-direction: column;
  }

  .admission-documents__header,
  .admission-documents__content {
    padding-right: 18px;
    padding-left: 18px;
  }

  .admission-documents__item {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .admission-documents__item-icon {
    display: none;
  }
}
</style>
